import { useState } from "react";
import type { ParseKeys } from "i18next";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import CustomerPageLayout from "../customer-page-layout/CustomerPageLayout";
import Button from "../../../common/ekedc-components/button/Button";
import PeriodFilter from "../components/period-filter/PeriodFilter";
import SelectMeter from "../components/select-meter/SelectMeter";
import { SuccessScreen } from "../components/success-screen";
import { VerifyNinModal } from "./components/VerifyNinModal";
import { PdfFormatIcon, XlsxIcon, InfoIcon, SharePdfIcon, PdfFillIcon, ExcelFillIcon } from "../../../assets/icons";
import {
	useGetCustomerMeterNumbersQuery,
	useGetAedcRelatedAccountsQuery,
	useGeneratePrepaidStatementOfAccountReportMutation,
	useGeneratePostpaidStatementOfAccountReportMutation,
} from "../../../service/vending-service";
import { ConsumerType, CustomerMeterNumber } from "../../../model/energy-profile";
import { AedcRelatedAccount } from "../../../model/vending/vending-interfaces";
import { useFeatureFlag, useFeatureFlags } from "../../../tenant/features/useFeatureFlag";
import { FeatureFlagName } from "../../../tenant/features/featureFlags";
import { useTenant } from "../../../tenant/branding/useTenant";
import { ExportFormat } from "../../../model/vending/vending-interfaces";
import { getErrorMessage } from "../../../utils/error-handler";
import { showLoader, hideLoader } from "../../../stores/slices/loader-slice";
import { RootState } from "../../../stores/store";
import { ApplicationRoutes } from "../../../routes";
import successGif from "../../../assets/gifs/success-gif.gif";
import styles from "./AccountActivities.module.scss";

const meterIdentifier = (m: CustomerMeterNumber): string =>
	m.consumerType === ConsumerType.Postpaid ? m.accountNumber : m.meterNumber;

// Prepaid is identified by its meter number, postpaid by its account number.
const toMeterNumber = (a: AedcRelatedAccount): CustomerMeterNumber => ({
	id: a.isPrepaid ? a.meterNumber : a.accountNumber,
	name: a.name ?? "",
	meterNumber: a.meterNumber,
	accountNumber: a.accountNumber,
	consumerType: a.isPrepaid ? ConsumerType.Prepaid : ConsumerType.Postpaid,
	residencyStatus: null,
	preferRemoteTokenLoad: false,
	isActive: true,
	pendingStatus: null,
});

// Backend expects date-only (yyyy-MM-dd); build from local parts to avoid a UTC off-by-one.
const toDateOnly = (date: Date | null | undefined): string => {
	if (!date) return "";
	const month = `${date.getMonth() + 1}`.padStart(2, "0");
	const day = `${date.getDate()}`.padStart(2, "0");
	return `${date.getFullYear()}-${month}-${day}`;
};

const PHASE_TYPE_OPTIONS: { value: string; labelKey: ParseKeys; flag: FeatureFlagName }[] = [
	{ value: "statement", labelKey: "account.web.statement", flag: FeatureFlagName.STATEMENT_OF_ACCOUNT },
	{ value: "historicalPayment", labelKey: "history.web.historicalPayment", flag: FeatureFlagName.HISTORICAL_PAYMENT },
];

const FORMAT_OPTIONS: { value: string; labelKey: ParseKeys; icon: JSX.Element }[] = [
	{ value: "pdf", labelKey: "account.web.pdf", icon: <PdfFillIcon /> },
	{ value: "excel", labelKey: "account.web.excel", icon: <ExcelFillIcon /> },
];

interface AccountActivitiesForm {
	meter: string;
	period: string | null;
	startDate: Date | null;
	endDate: Date | null;
	phaseType: string;
	format: string;
}

const InfoTooltip = ({ text }: { text: string }) => (
	<span className={styles.infoTooltipWrapper}>
		<InfoIcon className={styles.infoIcon} />
		<span className={styles.tooltip}>{text}</span>
	</span>
);

export const AccountActivitiesPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [showSuccess, setShowSuccess] = useState(false);
	
	// Generating a statement requires a verified NIN phone number; gate the page behind a prompt if not.
	const isNinPhoneNumberVerified = useSelector((state: RootState) => state.customerEnergyProfile.isNinPhoneNumberVerified);
	const isNinVerified = useSelector((state: RootState) => state.customerEnergyProfile.isNinVerified);

	// NIN verification is a per-tenant feature. When disabled for the tenant, never prompt for it.
	const isNinVerificationEnabled = useFeatureFlag("NinVerification");

	const { key: tenantKey } = useTenant();
	const isAedc = tenantKey === "aedc";

	const { isEnabled } = useFeatureFlags();

	const meterTypeLabel = (type: ConsumerType): string => {
		if (type === ConsumerType.Prepaid) return t("common.labels.prepaid");
		if (type === ConsumerType.Postpaid) return t("common.labels.postpaid");
		return "";
	};

	// aedc has no local meters — AEDC's related accounts are the source of truth there.
	const { data: localMeters = [], isLoading: isLoadingLocalMeters } = useGetCustomerMeterNumbersQuery(undefined, { skip: isAedc });
	const { data: relatedAccounts = [], isLoading: isLoadingRelatedAccounts } = useGetAedcRelatedAccountsQuery(undefined, { skip: !isAedc });

	const meters: CustomerMeterNumber[] = isAedc ? relatedAccounts.map(toMeterNumber) : localMeters;
	const isLoadingMeters = isAedc ? isLoadingRelatedAccounts : isLoadingLocalMeters;

	const meterOptions = meters.map(m => ({ id: m.id, name: m.name, meterNumber: meterIdentifier(m), type: meterTypeLabel(m.consumerType) }));

	const [generatePrepaidStatement, { isLoading: isGeneratingPrepaid }] = useGeneratePrepaidStatementOfAccountReportMutation();
	const [generatePostpaidStatement, { isLoading: isGeneratingPostpaid }] = useGeneratePostpaidStatementOfAccountReportMutation();
	const isGeneratingStatement = isGeneratingPrepaid || isGeneratingPostpaid;

	const { handleSubmit, watch, setValue, reset } = useForm<AccountActivitiesForm>({
		mode: "onChange",
		defaultValues: { meter: "", period: null, startDate: null, endDate: null, phaseType: "", format: "pdf" },
	});

	const meter = watch("meter");
	const period = watch("period");
	const startDate = watch("startDate");
	const endDate = watch("endDate");
	const watchedPhaseType = watch("phaseType");
	const format = watch("format");

	// A tenant may have only one of the two activities (aedc has statements only). When that's
	// the case there is nothing to choose, so the option card is hidden and the type is implied.
	const availablePhaseTypes = PHASE_TYPE_OPTIONS.filter(p => isEnabled(p.flag));
	const impliedPhaseType = availablePhaseTypes.length === 1 ? availablePhaseTypes[0].value : null;
	const phaseType = impliedPhaseType ?? watchedPhaseType;

	const selectedMeter = meters.find(m => m.id === meter);
	const isPostpaid = selectedMeter?.consumerType === ConsumerType.Postpaid;

	// Postpaid has no date range for either statement or historical payment — except on aedc,
	// whose statement provider always requires one.
	const isAedcPostpaidStatement = isAedc && isPostpaid && phaseType === "statement";
	const showPeriod = !!phaseType && (!isPostpaid || isAedcPostpaidStatement);

	const onSubmit = async (data: AccountActivitiesForm) => {
		// The form field is empty when the type is implied by the tenant's flags.
		const activePhaseType = impliedPhaseType ?? data.phaseType;

		if (activePhaseType === "historicalPayment") {
			navigate(`/${ApplicationRoutes.CUSTOMER_HISTORICAL_PAYMENT}`, {
				state: { meterId: data.meter, consumerType: selectedMeter?.consumerType, startDate: data.startDate, endDate: data.endDate },
			});
			return;
		}
		if (activePhaseType === "statement") {
			if (!selectedMeter) return;
			const exportFormat = data.format === "excel" ? ExportFormat.Excel : ExportFormat.Pdf;
			dispatch(showLoader());
			try {
				if (isPostpaid) {
					await generatePostpaidStatement({
						accountNumber: selectedMeter.accountNumber,
						from: isAedc ? toDateOnly(data.startDate) : undefined,
						to: isAedc ? toDateOnly(data.endDate) : undefined,
						exportFormat,
					}).unwrap();
				} else {
					await generatePrepaidStatement({
						meterNumber: selectedMeter.meterNumber,
						from: toDateOnly(data.startDate),
						to: toDateOnly(data.endDate),
						exportFormat,
					}).unwrap();
				}
				setShowSuccess(true);
			} catch (err: any) {
				toast.error(getErrorMessage(err?.data));
			} finally {
				dispatch(hideLoader());
			}
		}
	};

	return (
		<CustomerPageLayout title={t("home.web.accountActivities")} onBack={() => navigate(-1)}>
			<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
				<div className={styles.cards}>
					{/* Meter */}
					<section className={styles.section}>
						<div className={styles.sectionHeader}>
							<span className={styles.sectionTitle}>{t("account.web.meter")}</span>
						</div>
						<div className={styles.sectionBody}>
							<SelectMeter
								meters={meterOptions}
								selectedId={meter}
								placeholder={isLoadingMeters ? t("account.web.loadingMeters") : t("common.web.selectMeter")}
								disabled={isLoadingMeters}
								onSelect={m => {
									// Changing the meter starts fresh: reset every other field back to defaults.
									if (m.id === meter) return;
									reset({ meter: m.id, period: null, startDate: null, endDate: null, phaseType: "", format: "pdf" });
								}}
							/>
						</div>
					</section>

					{!impliedPhaseType && (
						<section className={styles.section}>
							<div className={styles.sectionHeader}>
								<span className={styles.sectionTitle}>{t("account.web.choosePhaseType")}</span>
							</div>
							<div className={styles.options}>
								{availablePhaseTypes.map(p => (
									<button
										type="button"
										key={p.value}
										className={`${styles.optionRow} ${phaseType === p.value ? styles.optionRowActive : ""}`}
										onClick={() => setValue("phaseType", p.value, { shouldValidate: true })}
									>
										<span className={styles.optionLabel}>{t(p.labelKey)}</span>
										<span className={`${styles.radio} ${phaseType === p.value ? styles.radioActive : ""}`} />
									</button>
								))}
							</div>
						</section>
					)}

					{/* Period (shared with the history filter page) — hidden for postpaid statements. */}
					{showPeriod && (
						<PeriodFilter
							title={t("history.period")}
							tooltipText={t("account.web.activitiesPeriodTooltip")}
							value={{ period, startDate, endDate }}
							onChange={next => {
								setValue("period", next.period);
								setValue("startDate", next.startDate);
								setValue("endDate", next.endDate);
							}}
							className={styles.periodCard}
						/>
					)}

					{/* Format — only for Statement */}
					{phaseType === "statement" && (
						<section className={styles.section}>
							<div className={styles.sectionHeader}>
								<span className={styles.sectionTitle}>{t("account.web.format")}</span>
								<InfoTooltip text={t("account.web.formatTooltip")} />
							</div>
							<div className={styles.formatGrid}>
								{FORMAT_OPTIONS.map(f => (
									<button
										type="button"
										key={f.value}
										className={`${styles.formatOption} ${format === f.value ? styles.formatOptionActive : ""}`}
										onClick={() => setValue("format", f.value, { shouldValidate: true })}
									>
										<span className={styles.formatIcon}>{f.icon}</span>
										<span className={styles.formatLabel}>{t(f.labelKey)}</span>
									</button>
								))}
							</div>
						</section>
					)}
				</div>

				<footer className={styles.footer}>
					<Button
						type="submit"
						text={t("common.actions.continueKey")}
						loading={isGeneratingStatement}
						disabled={!meter || !phaseType || (showPeriod && (!startDate || !endDate))}
					/>
				</footer>
			</form>

			{showSuccess && (
				<SuccessScreen
					imageSrc={successGif}
					imageAlt={t("account.web.successAlt")}
					title={t("energyManagement.energyProfile.youreAllSet")}
					subtitle={t("account.web.statementSentToEmail")}
					buttonText={t("common.pages.backToHome")}
					onContinue={() => navigate(`/${ApplicationRoutes.DASHBOARD}`)}
					secondaryButtonText={t("account.web.accountActivityPage")}
					onSecondary={() => {
						setShowSuccess(false);
						reset();
					}}
				/>
			)}

			{isNinVerificationEnabled &&
				(!isNinVerified ? (
					<VerifyNinModal mode="nin" onClose={() => navigate(-1)} />
				) : (
					!isNinPhoneNumberVerified && <VerifyNinModal mode="phone" onClose={() => navigate(-1)} />
				))
			}
		</CustomerPageLayout>
	);
};
